/**
 * Appointment Service Page
 * Modern, inline booking experience for appointment products
 * Inspired by Calendly/Cal.com - clean, professional, no popup modals
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import { useToast } from './ui/Toast';
import { useStorefrontCurrency } from '../contexts/StorefrontCurrencyContext';
import { getAllImageUrls } from '../utils/imageUtils';
import CustomerFacingSubtotal from './checkout/CustomerFacingSubtotal';

const AppointmentServicePage = ({ product, store, themeStyles, getStorePath }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { formatPrice } = useStorefrontCurrency();

  // Booking state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);

  // Calendar state
  const [viewingYear, setViewingYear] = useState(new Date().getFullYear());
  const [viewingMonth, setViewingMonth] = useState(new Date().getMonth());

  // Timezone
  const [customerTimezone, setCustomerTimezone] = useState('');

  // Payment breakdown
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  const settings = product?.appointmentSettings || {};
  const duration = settings.duration || 60;
  const isFree = !product?.price || parseFloat(product.price) === 0;
  const images = getAllImageUrls(product);
  const heroImage = images[0] || '/placeholder-service.jpg';

  // Theme accent color
  const accentColor = themeStyles?.primary || '#6366f1';

  // Detect timezone on mount
  useEffect(() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setCustomerTimezone(detected);
    } catch (e) {
      setCustomerTimezone('Africa/Lagos');
    }
  }, []);

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format display date
  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Generate calendar days
  const generateCalendarDays = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = new Date(viewingYear, viewingMonth, 1);
    const lastDay = new Date(viewingYear, viewingMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewingYear, viewingMonth, day);
      date.setHours(0, 0, 0, 0);

      days.push({
        date,
        isPast: date < today,
        isToday: date.getTime() === today.getTime()
      });
    }

    return days;
  }, [viewingYear, viewingMonth]);

  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Fetch available slots
  useEffect(() => {
    if (selectedDate && product?.id && customerTimezone) {
      const fetchSlots = async () => {
        try {
          setLoadingSlots(true);
          setError(null);
          const response = await axios.get('/appointments/available-slots', {
            params: {
              serviceId: product.id,
              date: formatDateForAPI(selectedDate),
              customerTimezone
            }
          });
          setAvailableSlots(response.data.availableSlots || []);
        } catch (err) {
          console.error('Error fetching slots:', err);
          setError('Failed to load available times');
          setAvailableSlots([]);
        } finally {
          setLoadingSlots(false);
        }
      };
      fetchSlots();
    }
  }, [selectedDate, product?.id, customerTimezone]);

  // Fetch payment breakdown
  useEffect(() => {
    if (currentStep === 3 && !isFree && product?.id) {
      const fetchBreakdown = async () => {
        try {
          setLoadingBreakdown(true);
          const response = await axios.get(`/appointments/payment-preview/${product.id}`);
          if (response.data?.success) {
            setPaymentBreakdown({
              subtotal: response.data.servicePrice || parseFloat(product.price),
              tax: response.data.taxAmount || 0,
              taxEnabled: response.data.taxEnabled === true,
              taxRate: response.data.taxRate || 0,
              taxName: response.data.taxName || 'VAT',
              serviceCharge: response.data.serviceCharge || 0,
              serviceChargeRate: response.data.serviceChargeRate || 0,
              serviceChargeWhoPays: response.data.serviceChargeWhoPays || response.data.whoPays || 'customer',
              serviceChargeEnabled: response.data.serviceChargeEnabled === true,
              total: response.data.total || parseFloat(product.price)
            });
          }
        } catch (err) {
          setPaymentBreakdown({
            subtotal: parseFloat(product.price),
            total: parseFloat(product.price)
          });
        } finally {
          setLoadingBreakdown(false);
        }
      };
      fetchBreakdown();
    }
  }, [currentStep, isFree, product?.id, product?.price]);

  // Handle date selection
  const handleDateSelect = (dayData) => {
    if (dayData && !dayData.isPast) {
      setSelectedDate(dayData.date);
      setSelectedTime('');
      setCurrentStep(2);
    }
  };

  // Handle time selection
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setCurrentStep(3);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle booking submission
  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      setError('Please select a date and time');
      return;
    }

    try {
      setBooking(true);
      setError(null);

      const response = await axios.post('/appointments', {
        serviceId: product.id,
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        appointmentDate: formatDateForAPI(selectedDate),
        appointmentTime: selectedTime,
        customerTimezone,
        notes: customerInfo.notes,
        paymentProvider: 'PAYSTACK'
      });

      if (response.data.success) {
        if (response.data.isFree) {
          setBookedAppointment(response.data.appointment);
          setBookingComplete(true);
          showToast('Appointment booked successfully!', 'success');
        } else if (response.data.payment) {
          localStorage.setItem('pendingAppointmentId', response.data.appointment.id);
          localStorage.setItem('pendingAppointmentReference', response.data.payment.reference);
          window.location.href = response.data.payment.authorizationUrl;
        }
      } else {
        setError(response.data.error || 'Failed to book appointment');
      }
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.error?.includes('not enabled')) {
        setError('Payment methods are not configured. Please contact the business.');
      } else {
        setError(err.response?.data?.error || 'Failed to book appointment');
      }
    } finally {
      setBooking(false);
    }
  };

  const isFormValid = customerInfo.name && customerInfo.email && customerInfo.phone && selectedDate && selectedTime;

  // Confirmation screen
  if (bookingComplete) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        {/* Decorative elements */}
        <div style={{
          position: 'fixed',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`,
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'fixed',
          bottom: '-150px',
          left: '-150px',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.02))',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '80px 24px',
          textAlign: 'center',
          position: 'relative'
        }}>
          {/* Success Icon with animation */}
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px',
            boxShadow: '0 20px 40px rgba(16, 185, 129, 0.3)',
            animation: 'asp-bounce 0.5s ease-out'
          }}>
            <svg width="48" height="48" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          {/* Confetti-like dots */}
          <div style={{ position: 'absolute', top: '60px', left: '20%', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: accentColor, opacity: 0.6 }} />
          <div style={{ position: 'absolute', top: '100px', right: '25%', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', opacity: 0.5 }} />
          <div style={{ position: 'absolute', top: '80px', right: '15%', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#f59e0b', opacity: 0.4 }} />

          <span style={{
            display: 'inline-block',
            padding: '6px 16px',
            backgroundColor: '#dcfce7',
            color: '#166534',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Confirmed
          </span>

          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            color: '#111827',
            margin: '0 0 16px',
            letterSpacing: '-0.02em'
          }}>
            You're All Set!
          </h1>

          <p style={{
            fontSize: '17px',
            color: '#6b7280',
            margin: '0 0 48px',
            lineHeight: '1.7'
          }}>
            Your appointment has been confirmed. We've sent the details to <strong style={{ color: '#374151' }}>{customerInfo.email}</strong>
          </p>

          {/* Appointment Details Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'left',
            marginBottom: '32px',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            {/* Service name header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              paddingBottom: '20px',
              borderBottom: '1px solid #f3f4f6',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}10)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" fill="none" stroke={accentColor} strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#111827' }}>
                  {product.name}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {store?.name || 'Appointment'}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="18" height="18" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', fontWeight: '500', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</p>
                  <p style={{ margin: '2px 0 0', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    {formatDisplayDate(selectedDate)}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="18" height="18" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', fontWeight: '500', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time</p>
                  <p style={{ margin: '2px 0 0', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    {selectedTime} ({duration} minutes)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Add to calendar hint */}
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '24px'
          }}>
            Add this appointment to your calendar to get reminders
          </p>

          <button
            onClick={() => navigate(getStorePath ? getStorePath() : '/')}
            style={{
              padding: '16px 40px',
              background: 'linear-gradient(135deg, #111827, #1f2937)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
            }}
          >
            Back to Store
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes asp-fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes asp-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes asp-bounce {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes asp-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .asp-step { animation: asp-fadeIn 0.25s ease-out; }
        .asp-day-btn:hover:not(:disabled) {
          background-color: #f3f4f6 !important;
          transform: scale(1.05);
        }
        .asp-day-btn { transition: all 0.15s ease !important; }
        .asp-time-btn:hover {
          background: linear-gradient(135deg, ${accentColor}, ${accentColor}dd) !important;
          color: white !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px ${accentColor}40 !important;
        }
        .asp-time-btn { transition: all 0.2s ease !important; }
        .asp-input:focus {
          border-color: ${accentColor} !important;
          box-shadow: 0 0 0 3px ${accentColor}20;
        }
        .asp-nav-btn:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
        @media (max-width: 900px) {
          .asp-main-grid { grid-template-columns: 1fr !important; }
          .asp-service-card { position: relative !important; top: auto !important; }
        }
        @media (max-width: 600px) {
          .asp-container { padding: 16px !important; }
          .asp-booking-card { padding: 24px 20px !important; }
          .asp-time-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: '-200px',
          right: '-200px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accentColor}08 0%, transparent 70%)`,
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        {/* Header */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          padding: '14px 24px',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={() => navigate(getStorePath ? getStorePath('shop') : '/shop')}
              className="asp-nav-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10b981'
              }} />
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                {store?.name || 'Online Booking'}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="asp-container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '40px 24px',
          position: 'relative'
        }}>
          <div className="asp-main-grid" style={{
            display: 'grid',
            gridTemplateColumns: '380px 1fr',
            gap: '40px',
            alignItems: 'start'
          }}>
            {/* Left: Service Info Card */}
            <div className="asp-service-card" style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              position: 'sticky',
              top: '90px',
              border: '1px solid rgba(0,0,0,0.04)'
            }}>
              {/* Service Image */}
              <div style={{
                aspectRatio: '16/10',
                backgroundColor: '#f3f4f6',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <img
                  src={heroImage}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                {/* Gradient overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '100px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                  pointerEvents: 'none'
                }} />
                {/* Badges */}
                <div style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  right: '16px',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <span style={{
                    padding: '8px 14px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    {duration} min
                  </span>
                  <span style={{
                    padding: '8px 14px',
                    backgroundColor: isFree ? '#10b981' : accentColor,
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    backdropFilter: 'blur(4px)'
                  }}>
                    {isFree ? 'Free' : formatPrice(product.price)}
                  </span>
                </div>
              </div>

              {/* Service Details */}
              <div style={{ padding: '24px' }}>
                {/* Category tag */}
                {product.category && (
                  <span style={{
                    display: 'inline-block',
                    padding: '5px 12px',
                    background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}08)`,
                    color: accentColor,
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}>
                    {product.category}
                  </span>
                )}

                <h1 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: '0 0 12px',
                  lineHeight: '1.3',
                  letterSpacing: '-0.01em'
                }}>
                  {product.name}
                </h1>

                {product.description && (
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    lineHeight: '1.7',
                    margin: '0 0 20px'
                  }}>
                    {product.description.length > 180
                      ? product.description.substring(0, 180) + '...'
                      : product.description}
                  </p>
                )}

                {/* What's included */}
                <div style={{
                  borderTop: '1px solid #f3f4f6',
                  paddingTop: '20px'
                }}>
                  <h4 style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 14px'
                  }}>
                    What's Included
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                      { icon: 'clock', text: `${duration} minute session` },
                      { icon: 'check', text: 'Instant confirmation' },
                      { icon: 'mail', text: 'Email reminder' },
                      { icon: 'calendar', text: 'Easy rescheduling' }
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          backgroundColor: '#f0fdf4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="14" height="14" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24">
                            {item.icon === 'clock' && <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>}
                            {item.icon === 'check' && <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>}
                            {item.icon === 'mail' && <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>}
                            {item.icon === 'calendar' && <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>}
                          </svg>
                        </div>
                        <span style={{ fontSize: '14px', color: '#374151' }}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Booking Flow */}
            <div className="asp-booking-card" style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '36px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.04)'
            }}>
              {/* Section label */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Schedule Your Appointment
                  </h2>
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: '2px 0 0' }}>
                    Choose a time that works for you
                  </p>
                </div>
              </div>

              {/* Progress Steps */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                marginBottom: '32px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px'
              }}>
                {[
                  { step: 1, label: 'Pick a Date', icon: 'calendar' },
                  { step: 2, label: 'Pick a Time', icon: 'clock' },
                  { step: 3, label: 'Your Details', icon: 'user' }
                ].map((item, index) => (
                  <React.Fragment key={item.step}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: currentStep === item.step ? 'white' : 'transparent',
                      boxShadow: currentStep === item.step ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: currentStep >= item.step
                          ? `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`
                          : '#e5e7eb',
                        color: currentStep >= item.step ? 'white' : '#9ca3af',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}>
                        {currentStep > item.step ? (
                          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : item.step}
                      </div>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: currentStep === item.step ? '600' : '500',
                        color: currentStep >= item.step ? '#111827' : '#9ca3af',
                        display: currentStep === item.step ? 'block' : 'none'
                      }}>
                        {item.label}
                      </span>
                    </div>
                    {index < 2 && (
                      <div style={{
                        width: '24px',
                        height: '2px',
                        backgroundColor: currentStep > item.step ? accentColor : '#e5e7eb',
                        borderRadius: '1px'
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {error && (
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  color: '#dc2626',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  {error}
                </div>
              )}

              {/* Step 1: Date Selection */}
              {currentStep === 1 && (
                <div className="asp-step">
                  <h3 style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 20px',
                    textAlign: 'center'
                  }}>
                    When would you like to meet?
                  </h3>

                  {/* Calendar Navigation */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '0 4px'
                  }}>
                    <button
                      onClick={() => {
                        if (viewingMonth === 0) {
                          setViewingMonth(11);
                          setViewingYear(viewingYear - 1);
                        } else {
                          setViewingMonth(viewingMonth - 1);
                        }
                      }}
                      className="asp-nav-btn"
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <svg width="18" height="18" fill="none" stroke="#374151" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>

                    <h3 style={{
                      fontSize: '17px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: 0
                    }}>
                      {monthNames[viewingMonth]} {viewingYear}
                    </h3>

                    <button
                      onClick={() => {
                        if (viewingMonth === 11) {
                          setViewingMonth(0);
                          setViewingYear(viewingYear + 1);
                        } else {
                          setViewingMonth(viewingMonth + 1);
                        }
                      }}
                      className="asp-nav-btn"
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <svg width="18" height="18" fill="none" stroke="#374151" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '6px',
                    padding: '4px'
                  }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} style={{
                        textAlign: 'center',
                        padding: '10px 0',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {day}
                      </div>
                    ))}

                    {calendarDays.map((dayData, index) => {
                      if (!dayData) return <div key={`empty-${index}`} />;

                      const isSelected = selectedDate && dayData.date.getTime() === selectedDate.getTime();

                      return (
                        <button
                          key={index}
                          className="asp-day-btn"
                          onClick={() => handleDateSelect(dayData)}
                          disabled={dayData.isPast}
                          style={{
                            padding: '12px 4px',
                            border: dayData.isToday && !isSelected ? `2px solid ${accentColor}40` : 'none',
                            background: isSelected
                              ? `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`
                              : 'transparent',
                            color: isSelected ? 'white' : dayData.isPast ? '#d1d5db' : '#111827',
                            borderRadius: '10px',
                            fontSize: '15px',
                            fontWeight: isSelected || dayData.isToday ? '600' : '500',
                            cursor: dayData.isPast ? 'not-allowed' : 'pointer',
                            boxShadow: isSelected ? `0 4px 12px ${accentColor}40` : 'none'
                          }}
                        >
                          {dayData.date.getDate()}
                        </button>
                      );
                    })}
                  </div>

                  {/* Today indicator */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginTop: '16px',
                    fontSize: '12px',
                    color: '#9ca3af'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '4px',
                      border: `2px solid ${accentColor}40`
                    }} />
                    <span>Today</span>
                  </div>
                </div>
              )}

              {/* Step 2: Time Selection */}
              {currentStep === 2 && (
                <div className="asp-step">
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '24px'
                  }}>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="asp-nav-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      Change Date
                    </button>
                  </div>

                  {/* Selected date display */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '14px 20px',
                    backgroundColor: `${accentColor}08`,
                    borderRadius: '12px',
                    marginBottom: '24px',
                    border: `1px solid ${accentColor}15`
                  }}>
                    <svg width="18" height="18" fill="none" stroke={accentColor} strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                      {formatDisplayDate(selectedDate)}
                    </span>
                  </div>

                  <h3 style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 20px',
                    textAlign: 'center'
                  }}>
                    What time works best?
                  </h3>

                  {loadingSlots ? (
                    <div style={{ textAlign: 'center', padding: '48px 0' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #f3f4f6',
                        borderTopColor: accentColor,
                        borderRadius: '50%',
                        animation: 'asp-spin 0.8s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                        Loading available times...
                      </p>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px 24px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '16px'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '16px',
                        backgroundColor: '#fee2e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}>
                        <svg width="28" height="28" fill="none" stroke="#dc2626" strokeWidth="1.5" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 8px' }}>
                        No times available
                      </p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        Please try a different date
                      </p>
                    </div>
                  ) : (
                    <>
                      <p style={{
                        fontSize: '13px',
                        color: '#9ca3af',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        {availableSlots.length} time{availableSlots.length !== 1 ? 's' : ''} available
                      </p>
                      <div className="asp-time-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '10px'
                      }}>
                        {availableSlots.map(time => (
                          <button
                            key={time}
                            className="asp-time-btn"
                            onClick={() => handleTimeSelect(time)}
                            style={{
                              padding: '16px 12px',
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#111827',
                              cursor: 'pointer'
                            }}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 3: Customer Details */}
              {currentStep === 3 && (
                <div className="asp-step">
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '24px'
                  }}>
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="asp-nav-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      Change Time
                    </button>
                  </div>

                  {/* Selected DateTime Summary */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '18px',
                    background: `linear-gradient(135deg, ${accentColor}08, ${accentColor}04)`,
                    borderRadius: '14px',
                    marginBottom: '28px',
                    border: `1px solid ${accentColor}15`
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: `0 4px 12px ${accentColor}30`
                    }}>
                      <svg width="22" height="22" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                        {formatDisplayDate(selectedDate)}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
                        {selectedTime} - {duration} minutes
                      </p>
                    </div>
                  </div>

                  <h3 style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 20px',
                    textAlign: 'center'
                  }}>
                    Almost there! Enter your details
                  </h3>

                  <form onSubmit={handleBooking}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Full Name <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={customerInfo.name}
                          onChange={handleInputChange}
                          required
                          placeholder="John Doe"
                          className="asp-input"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '15px',
                            outline: 'none',
                            transition: 'all 0.15s',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Email <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={customerInfo.email}
                          onChange={handleInputChange}
                          required
                          placeholder="john@example.com"
                          className="asp-input"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '15px',
                            outline: 'none',
                            transition: 'all 0.15s',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Phone <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={customerInfo.phone}
                          onChange={handleInputChange}
                          required
                          placeholder="+234 800 000 0000"
                          className="asp-input"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '15px',
                            outline: 'none',
                            transition: 'all 0.15s',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Notes <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span>
                        </label>
                        <textarea
                          name="notes"
                          value={customerInfo.notes}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Anything we should know beforehand?"
                          className="asp-input"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '15px',
                            outline: 'none',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            transition: 'all 0.15s',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Payment Summary */}
                      {!isFree && (
                        <div style={{
                          backgroundColor: '#f9fafb',
                          borderRadius: '14px',
                          padding: '20px',
                          marginTop: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px'
                          }}>
                            <svg width="18" height="18" fill="none" stroke="#374151" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="1" y="4" width="22" height="16" rx="2" />
                              <path d="M1 10h22" />
                            </svg>
                            <h4 style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#374151',
                              margin: 0
                            }}>
                              Payment Summary
                            </h4>
                          </div>

                          {loadingBreakdown ? (
                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #e5e7eb',
                                borderTopColor: accentColor,
                                borderRadius: '50%',
                                animation: 'asp-spin 0.8s linear infinite',
                                margin: '0 auto'
                              }} />
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <CustomerFacingSubtotal
                                subtotal={paymentBreakdown?.subtotal || product.price}
                                processingFee={
                                  paymentBreakdown?.serviceChargeEnabled
                                    && paymentBreakdown?.serviceCharge > 0
                                    && String(paymentBreakdown?.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                                    ? paymentBreakdown.serviceCharge
                                    : 0
                                }
                                formatCurrency={formatPrice}
                                compact
                                rowStyle={{ fontSize: '14px' }}
                                labelStyle={{ color: '#6b7280', fontWeight: 400 }}
                                valueStyle={{ color: '#6b7280', fontWeight: 400 }}
                                subLabel={false}
                              />

                              {paymentBreakdown?.taxEnabled && paymentBreakdown?.tax > 0 && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: '14px',
                                  color: '#6b7280'
                                }}>
                                  <span>{paymentBreakdown.taxName} ({paymentBreakdown.taxRate}%)</span>
                                  <span>{formatPrice(paymentBreakdown.tax)}</span>
                                </div>
                              )}

                              <div style={{
                                borderTop: '1px dashed #d1d5db',
                                paddingTop: '12px',
                                marginTop: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '17px',
                                fontWeight: '700',
                                color: '#111827'
                              }}>
                                <span>Total</span>
                                <span>{formatPrice(paymentBreakdown?.total || product.price)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={!isFormValid || booking}
                        style={{
                          width: '100%',
                          padding: '18px 24px',
                          background: (!isFormValid || booking)
                            ? '#e5e7eb'
                            : `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
                          color: (!isFormValid || booking) ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: (!isFormValid || booking) ? 'not-allowed' : 'pointer',
                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          transition: 'all 0.15s',
                          boxShadow: (!isFormValid || booking) ? 'none' : `0 8px 20px ${accentColor}30`
                        }}
                      >
                        {booking ? (
                          <>
                            <div style={{
                              width: '18px',
                              height: '18px',
                              border: '2px solid rgba(255,255,255,0.3)',
                              borderTopColor: 'white',
                              borderRadius: '50%',
                              animation: 'asp-spin 0.8s linear infinite'
                            }} />
                            Processing...
                          </>
                        ) : isFree ? (
                          <>
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Confirm Booking
                          </>
                        ) : (
                          <>
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="1" y="4" width="22" height="16" rx="2" />
                              <path d="M1 10h22" />
                            </svg>
                            Pay {formatPrice(paymentBreakdown?.total || product.price)}
                          </>
                        )}
                      </button>

                      {/* Security badge */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#9ca3af'
                      }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        Secure booking powered by Revstar
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppointmentServicePage;
